// Batch Service - Manages batch data and operations (Multi-tenant)
import { tenantService } from './tenantService';

export interface Batch {
  id: string;
  tenantId: string;
  name: string;
  breed: string;
  age: number;
  status: 'active' | 'completed' | 'cancelled';
  startDate: string;
  expectedEndDate: string;
  totalBirds: number;
  currentBirds: number;
  mortalityRate: number;
  feedConsumption: number;
  weight: number;
  location: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Additional properties for advanced tracking
  initialBirds?: number;
  currentWeight?: number;
  targetWeight?: number;
  mortality?: number;
  eggsCollected?: number;
  growthRecords?: GrowthRecord[];
  lastUpdated?: string;
  averageWeight?: number;
  averageUniformity?: number;
  averageFCR?: number;
}

export interface GrowthRecord {
  id: string;
  tenantId: string;
  batchId: string;
  date: string;
  age: number;
  weight: number;
  feedConsumption: number;
  mortality: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Additional properties for advanced growth tracking
  weights?: number[];
  averageWeight?: number;
  smallestWeight?: number;
  largestWeight?: number;
  weightRange?: number;
  uniformity?: number;
  standardWeight?: number;
  recordType?: 'daily' | 'weekly' | 'monthly' | 'summary';
  flock?: string;
  fcr?: number;
  totalBirds?: number;
  performance?: number;
  recordingMethod?: 'individual' | 'sample';
}

class BatchService {
  private batches: Batch[] = [];
  private growthRecords: GrowthRecord[] = [];

  constructor() {
    this.loadFromStorage();
  }

  // Reload data when tenant is ready (call this after tenantService is initialized)
  reloadForCurrentTenant(): void {
    this.loadFromStorage();
  }

  // Load data from localStorage
  private loadFromStorage(): void {
    try {
      // Use default tenant ID if tenantService is not ready
      const currentTenant = tenantService.getCurrentTenant();
      const tenantId = currentTenant?.id || 'default_tenant';
      const storageKey = `batches_${tenantId}`;
      const batchesData = localStorage.getItem(storageKey);
      if (batchesData) {
        this.batches = JSON.parse(batchesData);
      }

      const growthKey = `growthRecords_${tenantId}`;
      const growthData = localStorage.getItem(growthKey);
      if (growthData) {
        this.growthRecords = JSON.parse(growthData);
      }
    } catch (error) {
      console.error('Error loading data from storage:', error);
    }
  }

  // Save data to localStorage
  private saveToStorage(): void {
    try {
      // Use default tenant ID if tenantService is not ready
      const currentTenant = tenantService.getCurrentTenant();
      const tenantId = currentTenant?.id || 'default_tenant';
      const storageKey = `batches_${tenantId}`;
      const growthKey = `growthRecords_${tenantId}`;
      localStorage.setItem(storageKey, JSON.stringify(this.batches));
      localStorage.setItem(growthKey, JSON.stringify(this.growthRecords));
    } catch (error) {
      console.error('Error saving data to storage:', error);
    }
  }

  // Get all batches for current tenant
  getAllBatches(): Batch[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.batches.filter(batch => batch.tenantId === currentTenant.id);
  }

  // Get batch by ID
  getBatchById(id: string): Batch | undefined {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return undefined;
    
    return this.batches.find(batch => batch.id === id && batch.tenantId === currentTenant.id);
  }

  // Get active batches
  getActiveBatches(): Batch[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.batches.filter(batch => 
      batch.status === 'active' && batch.tenantId === currentTenant.id
    );
  }

  // Create new batch
  createBatch(batchData: Omit<Batch, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>): Batch {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) {
      throw new Error('No active tenant found');
    }

    const now = new Date().toISOString();
    const newBatch: Batch = {
      ...batchData,
      id: `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      tenantId: currentTenant.id,
      createdAt: now,
      updatedAt: now,
    };

    this.batches.push(newBatch);
    this.saveToStorage();
    return newBatch;
  }

  // Update batch
  updateBatch(id: string, updates: Partial<Batch>): Batch | null {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return null;
    
    const index = this.batches.findIndex(batch => 
      batch.id === id && batch.tenantId === currentTenant.id
    );
    if (index === -1) return null;

    this.batches[index] = { 
      ...this.batches[index], 
      ...updates, 
      updatedAt: new Date().toISOString() 
    };
    this.saveToStorage();
    return this.batches[index];
  }

  // Delete batch
  deleteBatch(id: string): boolean {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return false;
    
    const index = this.batches.findIndex(batch => 
      batch.id === id && batch.tenantId === currentTenant.id
    );
    if (index === -1) return false;

    this.batches.splice(index, 1);
    // Also remove related growth records
    this.growthRecords = this.growthRecords.filter(record => 
      record.batchId !== id && record.tenantId === currentTenant.id
    );
    this.saveToStorage();
    return true;
  }

  // Get growth records for a batch
  getGrowthRecords(batchId: string): GrowthRecord[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.growthRecords.filter(record => 
      record.batchId === batchId && record.tenantId === currentTenant.id
    );
  }

  // Add growth record
  addGrowthRecord(recordData: Omit<GrowthRecord, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>): GrowthRecord {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) {
      throw new Error('No active tenant found');
    }

    const now = new Date().toISOString();
    const newRecord: GrowthRecord = {
      ...recordData,
      id: `growth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      tenantId: currentTenant.id,
      createdAt: now,
      updatedAt: now,
    };

    this.growthRecords.push(newRecord);
    this.saveToStorage();
    return newRecord;
  }

  // Update growth record
  updateGrowthRecord(id: string, updates: Partial<GrowthRecord>): GrowthRecord | null {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return null;
    
    const index = this.growthRecords.findIndex(record => 
      record.id === id && record.tenantId === currentTenant.id
    );
    if (index === -1) return null;

    this.growthRecords[index] = { 
      ...this.growthRecords[index], 
      ...updates, 
      updatedAt: new Date().toISOString() 
    };
    this.saveToStorage();
    return this.growthRecords[index];
  }

  // Delete growth record
  deleteGrowthRecord(id: string): boolean {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return false;
    
    const index = this.growthRecords.findIndex(record => 
      record.id === id && record.tenantId === currentTenant.id
    );
    if (index === -1) return false;

    this.growthRecords.splice(index, 1);
    this.saveToStorage();
    return true;
  }

  // Get batch statistics for current tenant
  getBatchStatistics() {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) {
      return {
        totalBatches: 0,
        activeBatches: 0,
        completedBatches: 0,
        totalBirds: 0,
        averageMortality: 0,
      };
    }

    const tenantBatches = this.batches.filter(batch => batch.tenantId === currentTenant.id);
    const totalBatches = tenantBatches.length;
    const activeBatches = tenantBatches.filter(b => b.status === 'active').length;
    const completedBatches = tenantBatches.filter(b => b.status === 'completed').length;
    const totalBirds = tenantBatches.reduce((sum, batch) => sum + batch.currentBirds, 0);
    const averageMortality = tenantBatches.length > 0 
      ? tenantBatches.reduce((sum, batch) => sum + batch.mortalityRate, 0) / tenantBatches.length 
      : 0;

    return {
      totalBatches,
      activeBatches,
      completedBatches,
      totalBirds,
      averageMortality: Math.round(averageMortality * 100) / 100,
    };
  }

  // Get available batches for selection
  getAvailableBatches(): Batch[] {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return [];
    
    return this.batches.filter(batch => 
      batch.status === 'active' && batch.tenantId === currentTenant.id
    );
  }

  // Load batches (alias for getAllBatches for backward compatibility)
  loadBatches(): Batch[] {
    return this.getAllBatches();
  }

  // Add batch (alias for createBatch for backward compatibility)
  addBatch(batchData: Omit<Batch, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>): Batch {
    return this.createBatch(batchData);
  }

  // Clear all batches (for demo/reset purposes)
  clearAllBatches(): void {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return;

    this.batches = this.batches.filter(batch => batch.tenantId !== currentTenant.id);
    this.growthRecords = this.growthRecords.filter(record => record.tenantId !== currentTenant.id);
    this.saveToStorage();
  }

  // Reset to default batches (for demo purposes)
  resetToDefaultBatches(): void {
    const currentTenant = tenantService.getCurrentTenant();
    if (!currentTenant) return;

    // Clear existing batches for this tenant
    this.batches = this.batches.filter(batch => batch.tenantId !== currentTenant.id);
    this.growthRecords = this.growthRecords.filter(record => record.tenantId !== currentTenant.id);

    // Add some default batches
    const defaultBatches = [
      {
        name: 'Batch A - Cobb 500',
        breed: 'Cobb 500',
        age: 15,
        status: 'active' as const,
        startDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        expectedEndDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
        totalBirds: 1000,
        currentBirds: 950,
        mortalityRate: 5.0,
        feedConsumption: 150,
        weight: 0.8,
        location: 'House 1',
        initialBirds: 1000,
        currentWeight: 0.8,
        targetWeight: 2.5,
        mortality: 50,
      },
      {
        name: 'Batch B - Ross 308',
        breed: 'Ross 308',
        age: 8,
        status: 'active' as const,
        startDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        expectedEndDate: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString(),
        totalBirds: 800,
        currentBirds: 780,
        mortalityRate: 2.5,
        feedConsumption: 80,
        weight: 0.3,
        location: 'House 2',
        initialBirds: 800,
        currentWeight: 0.3,
        targetWeight: 2.3,
        mortality: 20,
      }
    ];

    defaultBatches.forEach(batchData => {
      this.createBatch(batchData);
    });
  }
}

// Export singleton instance
export const batchService = new BatchService();
